
import { useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { getSystemStats } from "@/services/fraudDetection/db/adminOperations";
import { ChartContainer } from "@/components/ui/chart";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart as LucideBarChart, Activity } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { logInfo, logError } from "@/utils/logger";

interface SystemStats {
  totalDetections: number;
  highRiskDetections: number;
  dailyDetections: Record<string, number>;
}

interface SystemStatsResponse {
  data: SystemStats | null;
  error: string | null;
  status: number;
}

const SystemAnalytics = () => {
  const { profile } = useAuth();

  const { data: stats, isLoading, error } = useQuery<SystemStatsResponse>({
    queryKey: ['systemStats', profile?.id],
    queryFn: async (): Promise<SystemStatsResponse> => {
      logInfo("Fetching system statistics", "SystemAnalytics", { adminId: profile?.id });
      if (!profile?.id) throw new Error("Admin profile not found");
      
      const response = await getSystemStats(profile.id);
      
      if (response.error) {
        logError("Failed to fetch system stats", "SystemAnalytics", { error: response.error });
        throw new Error(response.error);
      }
      
      logInfo("Successfully fetched system stats", "SystemAnalytics", { 
        adminId: profile?.id,
        statsReceived: !!response.data
      });
      
      return response as SystemStatsResponse;
    },
    enabled: !!profile?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes (previously cacheTime)
  });

  // Show toast on error
  useEffect(() => {
    if (error) {
      logError("SystemAnalytics query error", "SystemAnalytics", { error });
      toast({
        title: "Error",
        description: "Failed to load system analytics",
        variant: "destructive",
      });
    }
  }, [error]);

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Loading System Analytics...</CardTitle>
          <CardDescription>Please wait while we retrieve system-wide statistics</CardDescription>
        </CardHeader>
        <CardContent className="h-80 animate-pulse flex items-center justify-center">
          <Activity className="h-16 w-16 text-muted" />
        </CardContent>
      </Card>
    );
  }

  if (!stats?.data) {
    logInfo("No system stats data available", "SystemAnalytics");
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle>System Analytics</CardTitle>
          <CardDescription>No data available or access denied</CardDescription>
        </CardHeader>
        <CardContent className="h-80 flex items-center justify-center">
          <p className="text-muted-foreground">Unable to load system analytics data</p>
        </CardContent>
      </Card>
    );
  }

  // Prepare chart data
  const dailyData = stats?.data?.dailyDetections || {};
  const chartData = Object.entries(dailyData).map(([date, value]) => ({ 
    date, 
    value 
  }));

  // Calculate percentage of high risk detections
  const percentHighRisk = stats.data 
    ? ((stats.data.highRiskDetections / stats.data.totalDetections) * 100).toFixed(1) 
    : "0";

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>System Analytics</CardTitle>
        <CardDescription>Overall fraud detection system statistics</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Detections</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.data.totalDetections.toLocaleString() || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">High Risk Detections</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.data.highRiskDetections.toLocaleString() || 0}</div>
              <div className="text-xs text-muted-foreground mt-1">
                {percentHighRisk}% of total
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Daily Average</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {Math.round(
                  Object.values(stats.data.dailyDetections).reduce((sum, val) => sum + val, 0) / 
                  Object.keys(stats.data.dailyDetections).length
                )}
              </div>
            </CardContent>
          </Card>
        </div>
        
        <Tabs defaultValue="bar">
          <div className="flex justify-between items-center mb-4">
            <TabsList>
              <TabsTrigger value="bar">
                <LucideBarChart className="h-4 w-4 mr-2" />
                Bar Chart
              </TabsTrigger>
              <TabsTrigger value="line">Line Chart</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="bar" className="h-80">
            <ChartContainer config={{}}>
              <BarChart 
                width={500} 
                height={300} 
                data={chartData} 
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#6366f1" />
                <text 
                  x={250} 
                  y={20} 
                  fill="#888" 
                  textAnchor="middle" 
                  dominantBaseline="central"
                >
                  Daily Detections
                </text>
              </BarChart>
            </ChartContainer>
          </TabsContent>
          
          <TabsContent value="line" className="h-80">
            <ChartContainer config={{}}>
              <Activity className="h-full w-full text-primary" />
            </ChartContainer>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default SystemAnalytics;
